import React, { useEffect, useState } from "react";
import AvaibilityAnalysis from "./AvaibilityAnalysis";
import HotelStatus from "./HotelStatus";
import RevenueStatus from "./RevenueStatus";
import OccupancyChart from "./OccupancyChart";
import RevenueChart from "./RevenueChart";
import Bob from "./Bob";
import {
  Container,
  Typography,
  Box,
  Grid,
  Stack,
  Button,
  Tooltip,
  listItemSecondaryActionClasses,
} from "@mui/material";
import DateRangeComponent from "../../components/DateRangePicker";
import { CSVLink } from "react-csv";
import {
  availability_analysis_pms,
  bob_analysis_pms,
  getCurrentUser,
} from "../../api-config/config";
import moment from "moment";
import Source from "./Source";
import MarketAnalysis from "./MarketAnalysis";
import OtaPerformance from "./OtaPerformance";
import Demo from "./Demo";
import DemoData from "./DemoData";
import Pdf from "./Pdf";
import AvaibilityTable from "./AvaibilityTable";

const from_date: string = moment().format("YYYY-MM-DD");
const to_date: string = moment().add(1, "days").format("YYYY-MM-DD");

type AnalysisData = {
  data?: string;
  title?: string;
};

const ManagementDashboard = () => {
  const [loggedUser, setLoggedUser] = useState(getCurrentUser());
  const [loader, setLoader] = useState(false);
  const [range, setRange] = useState({
    startDate: new Date(),
    endDate: new Date(),
    key: "selection",
  });
  const [occupancyChartData, setOccupancyChartData] = useState([]);
  function onSetRange(value: any) {
    console.log(value);
    setRange(value);
  }
  const [arrData, setArrData] = useState<AnalysisData[]>([
    { data: "0.0", title: "ARR" },
    { data: "0.0%", title: "OCP" },
    { data: "0.0%", title: "RevPAR" },
  ]);
  const [bobData, setBobData] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);

  const isUserDataReady = !!loggedUser?.hotel_code;

  function searchWithDateRange() {
    if (!isUserDataReady) {
      console.error("Hotel code not available. Cannot fetch data.");
      return;
    }

    setDataLoading(true);
    const startDateString = `${range.startDate.getFullYear()}-${
      range.startDate.getMonth() + 1
    }-${range.startDate.getDate()}`;
    const endDateString = `${range.endDate.getFullYear()}-${
      range.endDate.getMonth() + 1
    }-${range.endDate.getDate()}`;

    Promise.all([
      fetchCheckOutOrder(startDateString, endDateString),
      bob_analysis_pms(startDateString, endDateString, loggedUser.hotel_code)
        .then((response: any) => {
          if (response && response.data) {
            setArrData([
              { data: response.data.ARR || "0.0", title: "ARR" },
              { data: response.data.OCP || "0.0%", title: "OCP" },
              { data: response.data.RevPAR || "0.0%", title: "RevPAR" },
            ]);
            setBobData(response.data.bob || []);
          } else if (response && response.message) {
            console.error("API Error:", response.message);
          }
          return response;
        })
        .catch((err) => {
          console.error("Error in bob_analysis_pms:", err);
        }),
    ]).finally(() => {
      setDataLoading(false);
    });
  }

  function fetchCheckOutOrder(startDateString: any, endDateString: any) {
    if (!isUserDataReady) {
      console.error("Hotel code not available. Cannot fetch data.");
      return Promise.resolve();
    }

    setLoader(true);
    return availability_analysis_pms(
      startDateString,
      endDateString,
      loggedUser.hotel_code
    )
      .then((response: any) => {
        setLoader(false);
        if (response && response.data) {
          setOccupancyChartData(response.data);
        } else if (response && response.message) {
          console.error("API Error:", response.message);
          setOccupancyChartData([]);
        }
        return response;
      })
      .catch((err) => {
        setLoader(false);
        console.error("Error in availability_analysis_pms:", err);
        setOccupancyChartData([]);
        return err;
      });
  }

  function getDateRange(sDate: any, eDate: any) {
    const currentMonth = sDate.getMonth();
    return {
      start: sDate.setMonth(currentMonth),
      end: eDate.setMonth(currentMonth),
    };
  }
  const checkOccdata = () => {
    return occupancyChartData?.length > 10 ? true : false;
  };

  useEffect(() => {
    if (!loggedUser) {
      const user = getCurrentUser();
      setLoggedUser(user);
      if (!user?.hotel_code) {
        console.error("User or hotel_code not available");
        setDataLoading(false);
        return;
      }
    }

    if (isUserDataReady) {
      const getObj = getDateRange(new Date(), new Date());
      const start = moment().format("YYYY-MM-DD");

      setDataLoading(true);
      Promise.all([
        fetchCheckOutOrder(from_date, start),
        bob_analysis_pms(from_date, start, loggedUser.hotel_code)
          .then((response: any) => {
            if (response && response.data) {
              setArrData([
                { data: response.data.ARR || "0.0", title: "ARR" },
                { data: response.data.OCP || "0.0%", title: "OCP" },
                { data: response.data.RevPAR || "0.0%", title: "RevPAR" },
              ]);
              setBobData(response.data.bob || []);
            } else if (response && response.message) {
              console.error("API Error:", response.message);
            }
            return response;
          })
          .catch((err) => {
            console.error("Error in initial bob_analysis_pms:", err);
          }),
      ]).finally(() => {
        setDataLoading(false);
      });
    }
  }, [loggedUser]);

  if (!isUserDataReady) {
    return (
      <div className="flex items-center justify-center h-screen">
        <p>Loading user data...</p>
      </div>
    );
  }

  return (
    <>
      <div
        style={{
          color: "#0323f8",
          marginLeft: 10,
          fontWeight: 600,
          fontSize: 22,
        }}
      >
        Management Dashboard
      </div>
      <Box
        sx={{
          // height: "100vh",
          width: "inherit",
          marginTop: 4,
          "& .super-app-theme--header": {
            backgroundColor: "#1976d2",
            color: "white",
          },
        }}
      >
        <p className="m-2.5">Select Date Range</p>
        <div className="flex justify-between p-2">
          <Stack direction="row" spacing={1} sx={{ justifyContent: "start" }}>
            <div className="border">
              <DateRangeComponent
                range={range}
                onSetRange={onSetRange}
                isShowPastDate={true}
              />
            </div>
            <Button
              type="button"
              onClick={() => searchWithDateRange()}
              className="bg-sky-600 hover:bg-sky-400 text-stone-50"
            >
              Search
            </Button>
          </Stack>
          {/* <div className="flex justify-end mx-4">
            <CSVLink 
            data={dynamicRows} filename={"Discount-Report-list.csv"}>
              <Tooltip title="Download Excel">
                <Button
                  type="button"
                  // onClick={() => searchWithDateRange()}
                  className="bg-sky-600 hover:bg-sky-400 text-stone-50"
                >
                  Download
                </Button>
              </Tooltip>
            </CSVLink>
          </div> */}
        </div>
      </Box>
      <div className="grid grid-cols-4 gap-5 h-72">
        {arrData.map((ele: any, i: any) => (
          <RevenueStatus key={i + 1} ele={ele} />
        ))}

        {/* <RevenueStatus />
        <RevenueStatus /> */}
        <HotelStatus startDate={range.startDate} endDate={range.endDate} />
      </div>
      <div
        className={`grid grid-cols-${checkOccdata() ? "1" : "2"} gap-5 my-7`}
      >
        <div style={{ marginBottom: "10px" }}>
          <OccupancyChart occupancyChartData={occupancyChartData} />
        </div>
        {!checkOccdata() ? (
          <div>
            <Bob bobData={bobData} />
          </div>
        ) : null}
      </div>

      {checkOccdata() ? (
        <div>
          <Bob bobData={bobData} />
        </div>
      ) : null}
      <div className="grid grid-cols-2 gap-5 ">
        <RevenueChart />
        <OtaPerformance />
      </div>

      {/* <div className="grid grid-cols-2 gap-5 my-7">
        <Source />
        <MarketAnalysis />
      </div> */}
      {/* <AvaibilityAnalysis /> */}
      {/* <Demo /> */}
      {/* <DemoData /> */}
      {/* <Pdf /> */}
      {/* <AvaibilityTable /> */}
    </>
  );
};

export default ManagementDashboard;
